import type {ReactElement} from 'react';
import {useEffect} from 'react';
import {SocialPlatform} from '@Components/social-media/social-media.types';
import {
  addConnectAccountBroadcastChannel,
  getAccountConnectionResponseFromBroadcast,
  getDisplayableSocialPlatformURL,
  getRedirectableSocialPlatformURL,
  getSocialPlatformName,
  handleAccountConnectionResponse,
  onConnectAccount,
  onConnectAccountForIosApp,
  removeConnectAccountBroadcastChannel,
} from '@Libraries/social-media';
import {Icon} from '@Components/icon-v2';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {SocialThumbnail} from '@Components/social-media/components/social-thumbnail';
import {Text, TextSize} from '@Components/text';
import {Button, Size, Type} from '@Components/button';
import {updateNumParentAccountsConnected, updateTriggerNext, updateTriggerPrevious} from '@Components/social-media-wizard/social-media-wizard-reducer';
import {useNumParentAccountsConnected} from '@Components/social-media-wizard/social-media-wizard.hooks';
import {GA4EventName, GA4EventParamName, trackPublishingGA4Events} from '@Libraries/ga-events';
import {isMobileOrIOSApp} from '@Utils/browser.util';
import {useAppDispatch} from '@/hooks';
import {isUserLoggedIn} from '@Libraries/user.library';
import {openAuthenticateModal} from '@Modals/authenticate-modal/authenticate-modal-library';
import {noop} from '@Utils/general.util';

interface SocialAccountConnectionProps {
  accountTypeToConnect: SocialPlatform;
}

function SocialAccountConnectionHeader({onBack, socialPlatform, accountType}: {onBack: () => void; accountType: SocialPlatform; socialPlatform: string}): ReactElement<any> {
  return (
    <div className="spacing-m-b-4 flex-row-align-center">
      <Icon icon="icon-arrow-left" size={IconSize.SIZE_ICON_20} type={IconType.GHOST} shape={IconShape.SQUARE} className="spacing-m-r-2" onClick={onBack} />
      <SocialThumbnail type={accountType} />
      <Text size={TextSize.SMALL} className="spacing-m-l-2" val={window.i18next.t('pmwjs_publish_to_x', {socialPlatform})} bold />
    </div>
  );
}

export function SocialAccountConnectionInstructions({
  accountType,
  onCancel,
  isFullWidth,
  onAccountConnected = noop,
}: {
  accountType: SocialPlatform;
  onCancel: () => void;
  isFullWidth: boolean;
  onAccountConnected?: () => void;
}): ReactElement<any> {
  const onAccountCreated = (): void => {
    onAccountConnected();
  };

  const startAccountConnection = (): void => {
    if (isMobileOrIOSApp()) {
      void onConnectAccountForIosApp(accountType, onAccountCreated);
      return;
    }
    onConnectAccount(accountType);
  };

  const onConnect = async (): Promise<void> => {
    if (isUserLoggedIn()) {
      startAccountConnection();
    } else {
      openAuthenticateModal({
        fbPermissions: '',
        signupSource: 'social-media-wizard',
        panelText: window.i18next.t('pmwjs_make_account_to_publish_post'),
        panelSubtext: window.i18next.t('pmwjs_login_publishing_promotion_subtext'),
        onLoginSuccess: async (): Promise<void> => {
          startAccountConnection();
        },
      });
    }
  };

  const getConnectionSetupText = (): string => {
    switch (accountType) {
      case SocialPlatform.INSTAGRAM:
        return window.i18next.t('pmwjs_instagram_connection_guide');
      default:
        return window.i18next.t('pmwjs_publish_go_to_x', {
          url: getRedirectableSocialPlatformURL(accountType),
          urlText: getDisplayableSocialPlatformURL(accountType),
        });
    }
  };

  const getConnectionCompleteText = (): string => {
    switch (accountType) {
      case SocialPlatform.INSTAGRAM:
        return window.i18next.t('pmwjs_instagram_connection_connection_message', {
          helpURL: 'https://support.postermywall.com/hc/en-us/articles/4410596800909-How-do-I-post-on-Instagram',
        });
      default:
        return window.i18next.t('pmwjs_connect_account');
    }
  };

  return (
    <>
      <div className={isFullWidth ? 'spacing-m-b-10' : 'spacing-m-b-4'}>
        <ol>
          <li>
            <Text val={getConnectionSetupText()} dangerouslySetInnerHTML size={TextSize.XSMALL} />
          </li>
          <li>
            <Text val={getConnectionCompleteText()} dangerouslySetInnerHTML size={TextSize.XSMALL} />
          </li>
        </ol>
      </div>
      <div className="flex-h-row flex-content-end">
        <Button
          customClasses={`spacing-m-r-2 ${isFullWidth ? '_full-width spacing-m-r-2' : ''} `}
          type={Type.SECONDARY}
          size={Size.SMALL}
          text={window.i18next.t('pmwjs_cancel')}
          onClick={onCancel}
        />
        <Button customClasses={isFullWidth ? '_full-width' : ''} type={Type.PRIMARY} size={Size.SMALL} text={window.i18next.t('pmwjs_connect')} onClick={onConnect} />
      </div>
    </>
  );
}

export function SocialAccountConnection({accountTypeToConnect}: SocialAccountConnectionProps): ReactElement<any> | null {
  const platformName = getSocialPlatformName(accountTypeToConnect);
  const numParentAccountsConnected = useNumParentAccountsConnected();
  const dispatch = useAppDispatch();
  const isAccountCreated = (broadcastEvent: MessageEvent): void => {
    const connectionResponse = getAccountConnectionResponseFromBroadcast(broadcastEvent);
    handleAccountConnectionResponse(connectionResponse, () => {
      if (!isMobileOrIOSApp()) {
        triggerWizardUpdatedOnAccountCreation();
      }
    });
  };

  const triggerWizardUpdatedOnAccountCreation = (): void => {
    dispatch(updateTriggerNext());
    dispatch(updateNumParentAccountsConnected(numParentAccountsConnected + 1));
    trackPublishingGA4Events(GA4EventName.ACCOUNT_ADDED, {[GA4EventParamName.PLATFORM]: platformName});
    trackPublishingGA4Events(GA4EventName.SOCIALWIZARD_ACCOUNTADDED, {[GA4EventParamName.PLATFORM]: platformName});
  };

  const onCancel = (): void => {
    dispatch(updateTriggerPrevious());
  };

  useEffect(() => {
    const accountCreatedBroadcastChannel = addConnectAccountBroadcastChannel(isAccountCreated);

    return (): void => {
      removeConnectAccountBroadcastChannel(accountCreatedBroadcastChannel);
    };
  }, []);

  return (
    <div className="flex-v-row">
      <SocialAccountConnectionHeader onBack={onCancel} accountType={accountTypeToConnect} socialPlatform={platformName} />
      <SocialAccountConnectionInstructions onAccountConnected={triggerWizardUpdatedOnAccountCreation} accountType={accountTypeToConnect} onCancel={onCancel} isFullWidth={false} />
    </div>
  );
}
